import {
  selectCurrentHotelId,
  selectCurrentHotelName,
  selectIsInternalAdmin
} from 'accessors'
import {
  PrimaryRegularButton,
  SecondaryRegularButton
} from 'components/ReusableComponents/Button/RegularButtons'
import ConfirmationDialog from 'components/ReusableComponents/ConfirmationDialog'
import Dialog from 'components/ReusableComponents/Dialog'
import FileIcon from 'components/ReusableComponents/FileIcon'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import CampaignSetup, {
  dripMediumOptions,
  dripMediumOptionsWithEmail,
  dripMediumOptionsWithText
} from 'features/CustomCampaigns/EditCampaign/CampaignSetup'
import {
  SequencePencilIcon,
  SequenceReadyIcon
} from 'features/CustomCampaigns/EditCampaign/CampaignFlowIcons'
import ChannelStrategyStep from 'features/CustomCampaigns/EditCampaign/ChannelStrategyStep'
import {
  getDefaultCampaignName,
  getDripMediumIcon,
  getDripMediumName
} from 'features/CustomCampaigns/EditCampaign/dripMediumMeta'
import PromoteLaunchStep from 'features/CustomCampaigns/EditCampaign/PromoteLaunchStep'
import PromoPreview from 'features/CustomCampaigns/EditCampaign/PromoteLaunchStep/PromoPreview'
import DripSequenceFlow from 'features/CustomCampaigns/EditCampaign/DripSequence/DripSequenceFlow'
import {
  applyMediumToSteps,
  createDefaultSequenceSteps,
  getStepDialogTitle,
  normalizeSequenceSteps,
  syncPrimaryStepToCampaign,
  validateSequenceSteps
} from 'features/CustomCampaigns/EditCampaign/DripSequence/dripSequenceHelpers'
import { useDripSequenceEnabled } from 'features/CustomCampaigns/EditCampaign/DripSequence/useDripSequenceEnabled'
import CampaignStartConfirmation from 'features/CustomCampaigns/EditCampaign/CampaignStartConfirmation'
import MainPromo from 'features/CustomCampaigns/EditCampaign/MainPromo'
import MainTemplate from 'features/CustomCampaigns/EditCampaign/MainTemplate'
import {
  customCampaignStatusTypes as statusTypes,
  getCampaignStatusColor
} from 'features/CustomCampaigns/helpers'
import CampaignEmailCustomizationSection from 'features/EmailEditor/CampaignEmailCustomizationSection'
import CampaignEmailDesignTemplatePicker from 'features/EmailEditor/CampaignEmailDesignTemplatePicker'
import {
  EmailEditorProvider,
  useEmailEditor
} from 'features/EmailEditor/EmailEditorContext'
import EmailPreview from 'features/EmailEditor/EmailPreview'
import TestEmail from 'features/EmailEditor/TestEmail'
import { getRequiredTagIdsForCustomCampaign } from 'features/ManageCampaigns/utils/campaignTagConfig'
import { hasMeaningfulCutoffDate } from 'features/CustomCampaigns/campaignAudienceDefaults'
import PromoUsageWarningDialog from 'features/ManageCampaigns/EditPromoDialog/PromoUsageWarningDialog'
import { TextPreview, TextPreviewBubble } from 'features/ManageCampaigns/TextPreview'
import {
  getExtensionFromUrl,
  getImgUrlFromCDN,
  isCampaignImageVisible
} from 'features/ManageCampaigns/utils/campaignHelpers'
import { mediaTags } from 'features/ManageCampaigns/utils/constants'
import useEsc from 'hooks/useEsc'
import usePlausible from 'hooks/usePlausible'
import usePromoValidation from 'hooks/usePromoValidaiton'
import moment from 'moment'
import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router'
import { Link } from 'react-router-dom'
import { useToasts } from 'react-toast-notifications'
import audienceApi from 'services/api/audience'
import campaignApi from 'services/api/campaign'
import { getApiErrorMessage } from 'services/helpers/campaignApiHelpers'

import { ReactComponent as CloseIcon } from './close.svg'
import styles from './EditCampaign.module.scss'

const steps = {
  channelStrategy: 'channelStrategy',
  campaignSetup: 'campaignSetup',
  dripSequence: 'dripSequence',
  mainTemplate: 'mainTemplate',
  email: 'email',
  mainPromo: 'mainPromo',
  promoteLaunch: 'promoteLaunch'
}

const redesignedSteps = [steps.channelStrategy, steps.dripSequence, steps.promoteLaunch]

function getSteps(medium, isSequenceEnabled, isRedesignEnabled) {
  if (isRedesignEnabled) {
    return redesignedSteps
  }

  const stepsArray = [steps.campaignSetup]

  if (isSequenceEnabled) {
    stepsArray.push(steps.dripSequence)
  } else {
    if (dripMediumOptionsWithText.includes(medium)) {
      stepsArray.push(steps.mainTemplate)
    }

    if (dripMediumOptionsWithEmail.includes(medium)) {
      stepsArray.push(steps.email)
    }
  }

  stepsArray.push(steps.mainPromo)
  return stepsArray
}

const urlRegex = /^(http|https)?:\/\/\S+/i

const Dot = ({ fill = 'gray' }) => (
  <svg height="8" width="7">
    <circle cx="3.5" cy="3.5" r="3.5" fill={fill} />
  </svg>
)

const mediaTag = mediaTags.campaign

const requiredTags = ['@[hotelName](__hotelName__)', '@[guestName](__guestName__)']

const EditCampaign = () => {
  const { id } = useParams()
  const history = useHistory()

  const seededCampaignFromNavigation =
    history.location.state?.campaign &&
    Number(history.location.state.campaign.contentId) === Number(id)
      ? history.location.state.campaign
      : null

  const [campaignData, setCampaignData] = useState(seededCampaignFromNavigation)

  const [isLoadingCampaign, setIsLoadingCampaign] = useState(
    !seededCampaignFromNavigation
  )
  const [isLoadingAudience, setIsLoadingAudience] = useState(true)
  const [deliveryStartDate, setDeliveryStartDate] = useState()
  const [deliveryFinishDate, setDeliveryFinishDate] = useState()

  const [promotionCode, setPromotionCode] = useState()
  const [maxDiscountPercentage, setMaxDiscountPercentage] = useState()
  const [minNightsToStay, setMinNightsToStay] = useState()
  const [promoTagline, setPromoTagline] = useState('Sample promo taglne')

  const [hasPromoValidityDate, setHasPromoValidityDate] = useState(false)
  const [promoValidityStartDate, setPromoValidityStartDate] = useState()
  const [promoValidityEndDate, setPromoValidityEndDate] = useState()
  const [showPromoUsageWarning, setShowPromoUsageWarning] = useState(null)

  const [messageMedia, setMessageMedia] = useState()

  const [customDeliveryStatus, setCustomDeliveryStatus] = useState()

  const [customUrl, setCustomUrl] = useState()

  const [audiences, setAudiences] = useState([])

  const [canModifySetup, setCanModifySetup] = useState(false)

  const { postEvent, plausibleEvents } = usePlausible()

  const isInternalAdmin = useSelector(selectIsInternalAdmin)

  const [medium, setMedium] = useState()

  const [deliveryName, setDeliveryName] = useState('')
  const [isEditingDeliveryName, setIsEditingDeliveryName] = useState(false)

  const isDripSequenceFeatureEnabled = useDripSequenceEnabled()
  const isRedesignEnabled = isDripSequenceFeatureEnabled
  const [isSequenceEnabled, setIsSequenceEnabled] = useState(false)
  const [sequenceSteps, setSequenceSteps] = useState([])
  const [isSaving, setIsSaving] = useState(false)
  const [campaignLoadError, setCampaignLoadError] = useState(false)
  const [existingCampaignNames, setExistingCampaignNames] = useState([])

  useEffect(() => {
    const seededCampaign =
      history.location.state?.campaign &&
      Number(history.location.state.campaign.contentId) === Number(id)
        ? history.location.state.campaign
        : null

    setCampaignLoadError(false)

    if (seededCampaign) {
      setCampaignData(seededCampaign)
      setIsLoadingCampaign(false)
    } else {
      setIsLoadingCampaign(true)
    }

    campaignApi
      .getCustomCampaignList()
      .then(res => {
        setExistingCampaignNames(
          res.items
            .filter(c => c.contentId !== Number(id))
            .map(c => c.deliveryName)
            .filter(Boolean)
        )

        const campaign = res.items.find(c => c.contentId === Number(id))
        if (campaign) {
          setCampaignData(campaign)
          setCampaignLoadError(false)
          return
        }

        if (!seededCampaign) {
          setCampaignData(undefined)
          setCampaignLoadError(true)
        }
      })
      .catch(err => {
        console.warn(err)
        if (!seededCampaign) {
          setCampaignLoadError(true)
        }
      })
      .finally(() => {
        setIsLoadingCampaign(false)
      })
  }, [id, isInternalAdmin, history.location.state])

  useEffect(() => {
    setIsLoadingAudience(true)

    audienceApi
      .getList()
      .then(res => {
        setAudiences(res)
      })
      .catch(err => {
        console.warn(err)
      })
      .finally(() => {
        setIsLoadingAudience(false)
      })
  }, [])

  useEffect(() => {
    if (campaignData) {
      const c = campaignData
      const template = c.messageTemplate || ''

      setAudienceId(c.audienceId)
      setPromotionCode(c.promotionCode)
      setPromoTagline(c.promotionTagline)
      setMinNightsToStay(c.minNightsToStay)
      setMaxDiscountPercentage(c.maxDiscountPercentage)
      setMedium(c.medium)
      setDeliveryName(c.deliveryName || '')

      const sequenceEnabled = isRedesignEnabled || Boolean(c.isSequenceEnabled)
      setIsSequenceEnabled(sequenceEnabled)
      setSequenceSteps(normalizeSequenceSteps(c.sequenceSteps, c))

      setDeliveryStartDate(c.deliveryStartDate || moment().toDate())
      setDeliveryFinishDate(
        hasMeaningfulCutoffDate(c.deliveryFinishDate) ? c.deliveryFinishDate : null
      )

      setMessageTemplate(template)
      setMessageMedia(c.messageMediaUrls?.[0])

      setUsedAllTags(requiredTags.every(tag => template.includes(tag)))

      setCustomDeliveryStatus(c.customDeliveryStatus)

      setCustomUrl(c.customUrl)

      setHasPromoValidityDate(c.hasPromoValidityDate)
      setPromoValidityStartDate(
        c.promoValidityStartDate ? new Date(c.promoValidityStartDate) : new Date()
      )
      setPromoValidityEndDate(
        c.promoValidityEndDate
          ? new Date(c.promoValidityEndDate)
          : moment().add(15, 'day').toDate()
      )

      if ([statusTypes.Draft, statusTypes.Approved].includes(c.customDeliveryStatus)) {
        setCanModifySetup(true)
      }

      if (c.customDeliveryStatus === statusTypes.Approved) {
        setActiveStep(isRedesignEnabled ? steps.dripSequence : steps.mainTemplate)
      }
    }
  }, [campaignData, isRedesignEnabled])

  const { emailState, saveChanges, refreshEmailData } = useEmailEditor()

  function getCustomCampaignUpdatePayload(customDeliveryStatus, sequenceStepsOverride) {
    const effectiveSequenceSteps = sequenceStepsOverride ?? sequenceSteps
    const primaryStepFields = isSequenceEnabled
      ? syncPrimaryStepToCampaign(effectiveSequenceSteps)
      : {}

    return {
      ...campaignData,
      audienceId,
      deliveryName: deliveryName.trim() || campaignData.deliveryName,
      deliveryStartDate: moment(deliveryStartDate).format('YYYY-MM-DD'),
      deliveryFinishDate: hasMeaningfulCutoffDate(deliveryFinishDate)
        ? moment(deliveryFinishDate).format('YYYY-MM-DD')
        : null,
      messageTemplate: primaryStepFields.messageTemplate ?? messageTemplate,
      messageMediaUrls:
        primaryStepFields.messageMediaUrls ?? (messageMedia ? [messageMedia] : []),
      customDeliveryStatus: customDeliveryStatus || campaignData.customDeliveryStatus,
      customUrl,
      promotionCode,
      promotionTagline: promoTagline,
      maxDiscountPercentage,
      minNightsToStay,
      hasPromoValidityDate,
      promoValidityStartDate: moment(promoValidityStartDate).format('YYYY-MM-DD'),
      promoValidityEndDate: moment(promoValidityEndDate).format('YYYY-MM-DD'),
      medium,
      isSequenceEnabled,
      sequenceSteps: isSequenceEnabled
        ? applyMediumToSteps(effectiveSequenceSteps, medium)
        : null,
      isActive:
        (customDeliveryStatus || campaignData.customDeliveryStatus) === statusTypes.Draft
          ? false
          : true
    }
  }

  async function persistDraftBeforeTestSms(sequenceStepsOverride) {
    if (!campaignData) {
      throw new Error('Campaign not loaded')
    }

    if (Array.isArray(sequenceStepsOverride)) {
      setSequenceSteps(sequenceStepsOverride)
    }

    const result = await campaignApi.updateCustomCampaign(
      getCustomCampaignUpdatePayload(
        campaignData.customDeliveryStatus,
        sequenceStepsOverride
      )
    )

    if (result && typeof result === 'object') {
      setCampaignData(prev => (prev ? { ...prev, ...result } : prev))
    }
  }

  function updateCampaign(customDeliveryStatus, options = {}) {
    const { reloadCampaignsList = false } = options

    if (isSaving) {
      return Promise.resolve()
    }

    if (isSequenceEnabled && !sequenceValidation.isValid) {
      const firstInvalidStepId = Object.keys(sequenceValidation.errors)[0]
      const stepTitle = getStepDialogTitle(firstInvalidStepId)
      addToast(
        `Please fix "${stepTitle}": ${sequenceValidation.errors[firstInvalidStepId]}`,
        {
          appearance: 'error',
          autoDismiss: true
        }
      )
      setActiveStep(steps.dripSequence)
      return Promise.resolve()
    }

    postEvent(plausibleEvents.UpdateTextBlast, { id })

    function conditionalEmailUpdate() {
      if (dripMediumOptionsWithEmail.includes(medium)) {
        const { subject, preheaderText } = emailState

        if (!subject) {
          addToast('Email subject cannot be empty', {
            appearance: 'error',
            autoDismiss: true
          })
          return Promise.reject('Email subject cannot be empty')
        }

        if (!preheaderText) {
          addToast('Email preheader cannot be empty', {
            appearance: 'error',
            autoDismiss: true
          })
          return Promise.reject('Email preheader cannot be empty')
        }

        return saveChanges()
      } else {
        return Promise.resolve()
      }
    }

    setIsSaving(true)

    return conditionalEmailUpdate()
      .then(() =>
        campaignApi.updateCustomCampaign(
          getCustomCampaignUpdatePayload(customDeliveryStatus)
        )
      )
      .then(result => {
        if (result && typeof result === 'object' && campaignData) {
          setCampaignData(prev => (prev ? { ...prev, ...result } : prev))
          if (typeof result.medium === 'string') {
            setMedium(result.medium)
          }
        }
        addToast('Campaign updated successfully', {
          appearance: 'success',
          autoDismiss: true
        })
        if (reloadCampaignsList) {
          history.push('/custom-campaigns')
        }
      })
      .catch(err => {
        console.warn(err)
        if (typeof err !== 'string') {
          addToast(
            getApiErrorMessage(err) || 'Failed to update campaign. Please try again.',
            {
              appearance: 'error',
              autoDismiss: true
            }
          )
        }
      })
      .finally(() => {
        setIsSaving(false)
      })
  }

  const wrapperRef = useRef()

  useEsc(() => {
    if (wrapperRef.current.offsetParent !== null) {
      history.push('/custom-campaigns')
    }
  })

  const { addToast } = useToasts()

  const userName = useSelector(s => s.login.currentUser.name)
  const hotelName = useSelector(selectCurrentHotelName)
  const hotelId = useSelector(selectCurrentHotelId)
  const requiredTagIds = getRequiredTagIdsForCustomCampaign(hotelId)
  const isHotelSetupCompleted = useSelector(s => s.hotelDetails.isPreOnboardingCompleted)

  const [messageTemplate, setMessageTemplate] = useState('')

  const [usedAllTags, setUsedAllTags] = useState(true)

  const [audienceId, setAudienceId] = useState()

  const [isAudienceCalculating, setIsAudienceCalculating] = useState(true)

  useEffect(() => {
    if (audienceId) {
      setIsAudienceCalculating(true)

      audienceApi.get(audienceId).then(res => {
        setIsAudienceCalculating(false)
        setAudiences(prev => prev.map(a => (a.id === res.id ? { ...a, ...res } : a)))
      })
    }
  }, [audienceId])

  function handleTextChange(text) {
    setMessageTemplate(text.replace(/inviteLink__\)(\S)/, 'inviteLink__) $1'))
  }

  const [isConfirmationVisible, setIsConfirmationVisible] = useState()

  const stepNames = {
    channelStrategy: 'Channel Strategy',
    campaignSetup: 'Preferences',
    dripSequence: 'Sequence',
    mainTemplate: 'Text content',
    email: 'Email',
    mainPromo: 'Promotion',
    promoteLaunch: 'Promote & Launch',
    optInPromo: 'Opt-in promotion'
  }

  const [activeStep, setActiveStep] = useState(
    isRedesignEnabled ? steps.channelStrategy : steps.campaignSetup
  )

  const [nextStep, setNextStep] = useState(steps.mainTemplate)
  const [isNextDisabled, setIsNextDisabled] = useState(false)

  const [isValidated, setIsValidated] = useState()

  const sequenceValidation = validateSequenceSteps(sequenceSteps, medium)

  useEffect(() => {
    const isSetupGood = deliveryStartDate || !canModifySetup
    const isMainTemplateGood = isSequenceEnabled
      ? sequenceValidation.isValid
      : usedAllTags
    const isChannelGood = !isRedesignEnabled || Boolean(medium)

    setIsValidated(isSetupGood && isMainTemplateGood && isChannelGood)
  }, [
    deliveryFinishDate,
    deliveryStartDate,
    usedAllTags,
    activeStep,
    canModifySetup,
    isSequenceEnabled,
    isRedesignEnabled,
    medium,
    sequenceValidation.isValid
  ])

  useEffect(() => {
    let isGood

    if (activeStep === steps.channelStrategy) {
      isGood = Boolean(medium) && Boolean(deliveryName.trim())
    }

    if (activeStep === steps.campaignSetup) {
      isGood = deliveryStartDate
    }

    if (activeStep === steps.dripSequence) {
      isGood = sequenceValidation.isValid
    }

    if (activeStep === steps.mainTemplate) {
      isGood = usedAllTags && (customUrl ? !!customUrl.match(urlRegex) : true)
    }

    if (activeStep === steps.mainPromo) {
      isGood = true
    }

    if (activeStep === steps.promoteLaunch) {
      isGood = Boolean(deliveryStartDate)
    }

    if (activeStep === steps.email) {
      isGood = true
    }

    setIsNextDisabled(!isGood)
  }, [
    deliveryFinishDate,
    deliveryStartDate,
    deliveryName,
    medium,
    usedAllTags,
    activeStep,
    canModifySetup,
    customUrl,
    sequenceValidation.isValid
  ])

  useEffect(() => {
    function getRedesignedNextStep() {
      const currentIndex = redesignedSteps.indexOf(activeStep)
      return redesignedSteps[currentIndex + 1] ?? null
    }

    function getNextStep() {
      if (isRedesignEnabled) {
        return getRedesignedNextStep()
      }

      switch (activeStep) {
        case steps.campaignSetup:
          if (isSequenceEnabled) {
            return steps.dripSequence
          }

          if (dripMediumOptionsWithText.includes(medium)) {
            return steps.mainTemplate
          }

          return steps.email

        case steps.dripSequence:
          return steps.mainPromo

        case steps.mainTemplate:
          if (dripMediumOptionsWithEmail.includes(medium)) {
            return steps.email
          }

          return steps.mainPromo

        case steps.email:
          return steps.mainPromo

        case steps.mainPromo:
          return null

        default:
          return null
      }
    }

    setNextStep(getNextStep())
  }, [activeStep, medium, isSequenceEnabled, isRedesignEnabled])

  useEffect(() => {
    if (isSequenceEnabled && sequenceSteps.length === 0 && campaignData) {
      setSequenceSteps(createDefaultSequenceSteps(campaignData))
    }
  }, [isSequenceEnabled, sequenceSteps.length, campaignData])

  useEffect(() => {
    if (!isRedesignEnabled || !medium || deliveryName.trim()) {
      return
    }

    setDeliveryName(getDefaultCampaignName(medium, existingCampaignNames))
  }, [isRedesignEnabled, medium, deliveryName, existingCampaignNames])

  useEffect(() => {
    if (!isSequenceEnabled) return

    const primaryFields = syncPrimaryStepToCampaign(sequenceSteps)
    if (primaryFields.messageTemplate) {
      setMessageTemplate(primaryFields.messageTemplate)
    }
    if (primaryFields.messageMediaUrls?.[0]) {
      setMessageMedia(primaryFields.messageMediaUrls[0])
    }
  }, [sequenceSteps, isSequenceEnabled])

  const [isMissingMediaWarningVisible, setIsMissingMediaWarningVisible] = useState(false)

  async function startCampaign() {
    postEvent(plausibleEvents.StartTextBlast, { id })

    setIsMissingMediaWarningVisible(false)

    updateCampaign(statusTypes.Approved)

    // setTimeout(() => {
    //   html2canvas(document.querySelector('#text-blast-estimation'))
    //     .then(canvas =>
    //       campaignApi.postCampaignEstimationScreenshot({
    //         contentId: id,
    //         screenshot: canvas.toDataURL('image/png', 1)
    //       })
    //     )
    //     .then(() => {
    //       updateCampaign(statusTypes.Approved)
    //     })
    // }, 100)
  }

  async function checkMediaAndStartCampaign() {
    if (moment(deliveryStartDate).isBefore(moment().startOf('day'))) {
      addToast('Start date should be in the future', {
        appearance: 'error',
        autoDismiss: true
      })
      return
    }

    if (
      hasMeaningfulCutoffDate(deliveryFinishDate) &&
      moment(deliveryFinishDate).isBefore(moment().startOf('day'))
    ) {
      addToast('End date should be in the future', {
        appearance: 'error',
        autoDismiss: true
      })
      return
    }

    if (
      hasMeaningfulCutoffDate(deliveryFinishDate) &&
      moment(deliveryFinishDate).isBefore(moment(deliveryStartDate))
    ) {
      addToast('End date should be after start date', {
        appearance: 'error',
        autoDismiss: true
      })
      return
    }

    if (!isHotelSetupCompleted) {
      addToast(
        'Your initial payment is pending. Please complete it to use drip campaigns.',
        {
          appearance: 'error',
          autoDismiss: true
        }
      )
      return
    }

    startCampaign()

    history.push('/custom-campaigns')

    // if (messageMedia) {
    //   startCampaign()
    // } else {
    //   setIsMissingMediaWarningVisible(true)
    // }
  }

  const { checkRateCodeUsage } = usePromoValidation(promotionCode)

  async function handleNext() {
    if (activeStep === steps.dripSequence && !sequenceValidation.isValid) {
      const firstInvalidStepId = Object.keys(sequenceValidation.errors)[0]
      const stepTitle = getStepDialogTitle(firstInvalidStepId)
      addToast(
        `Please fix "${stepTitle}": ${sequenceValidation.errors[firstInvalidStepId]}`,
        {
          appearance: 'error',
          autoDismiss: true
        }
      )
      return
    }

    if (
      (activeStep === steps.mainPromo || activeStep === steps.promoteLaunch) &&
      promotionCode
    ) {
      const { messageType } = await checkRateCodeUsage(promotionCode)

      if (!messageType) {
        setActiveStep(nextStep)
      }

      setShowPromoUsageWarning(messageType)
    } else {
      setActiveStep(nextStep)
    }
  }

  if (showPromoUsageWarning) {
    return (
      <PromoUsageWarningDialog
        softMessage={true}
        promoCode={promotionCode}
        onClose={() => setShowPromoUsageWarning(false)}
        onConfirm={promotionCode}
        showPromoUsageWarning={showPromoUsageWarning}
      />
    )
  }

  if (isLoadingCampaign || isLoadingAudience) {
    return <LoadingIndicator />
  }

  if (!campaignData || campaignLoadError) {
    return (
      <ConfirmationDialog
        title="Campaign not found"
        hasConfirmButton={true}
        hasCloseButton={true}
        positiveMessage="Back to campaigns"
        onConfirm={() => history.push('/custom-campaigns')}
        onClose={() => history.push('/custom-campaigns')}
        onReject={() => history.push('/custom-campaigns')}
        theme="blacklist"
      >
        <p>This campaign could not be loaded. Return to the list and try again.</p>
      </ConfirmationDialog>
    )
  }

  if (isMissingMediaWarningVisible) {
    return (
      <ConfirmationDialog
        title="Do you want to start the campaign without a media?"
        onConfirm={() => startCampaign()}
        onClose={() => {
          setIsMissingMediaWarningVisible(false)
          setActiveStep(steps.mainTemplate)
        }}
        positiveMessage="Yes, start without media"
        negativeMessage="No, go back"
        hasDangerousAction
      >
        <p>
          Adding an image or video is <strong>strongly recommended</strong> to get the
          best results from drip campaigns.
        </p>
      </ConfirmationDialog>
    )
  }

  if (isConfirmationVisible) {
    return (
      <CampaignStartConfirmation
        startDate={deliveryStartDate}
        deliveryName={campaignData?.deliveryName}
        handleConfirm={() => startCampaign()}
        handleCancel={() => setIsConfirmationVisible(false)}
        audienceId={audienceId}
      />
    )
  }

  const isSequenceStepActive =
    activeStep === steps.dripSequence || activeStep === steps.channelStrategy
  const isSidePreviewVisible = !isRedesignEnabled && !isSequenceStepActive

  const wizardSteps = getSteps(medium, isSequenceEnabled, isRedesignEnabled)
  const activeStepIndex = wizardSteps.indexOf(activeStep)
  const MediumPillIcon = getDripMediumIcon(medium)

  return (
    <Dialog verticalPadding={30}>
      <div
        className={[
          isRedesignEnabled
            ? styles.resolutionWrapperWizard
            : isSequenceStepActive
            ? styles.resolutionWrapperSequence
            : styles.resolutionWrapper,
          activeStep === steps.email ? styles.resolutionWrapperEmail : ''
        ]
          .filter(Boolean)
          .join(' ')}
        ref={wrapperRef}
      >
        <div
          className={[
            styles.dialogWrapper,
            isRedesignEnabled ? styles.dialogWrapperWizard : '',
            !isRedesignEnabled && isSequenceStepActive ? styles.dialogWrapperSequence : ''
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <div
            className={
              isRedesignEnabled
                ? `${styles.dialogHeader} ${styles.dialogHeaderWizard}`
                : styles.dialogHeader
            }
          >
            <div className={styles.dialogDesc}>
              {isRedesignEnabled ? (
                <span className={styles.deliveryNameEditor}>
                  {isEditingDeliveryName ? (
                    <input
                      className={styles.deliveryNameInput}
                      value={deliveryName}
                      autoFocus
                      maxLength={80}
                      aria-label="Campaign name"
                      onChange={e => setDeliveryName(e.target.value)}
                      onBlur={() => setIsEditingDeliveryName(false)}
                      onKeyDown={e =>
                        e.key === 'Enter' && setIsEditingDeliveryName(false)
                      }
                    />
                  ) : (
                    <button
                      type="button"
                      className={styles.deliveryNameButton}
                      onClick={() => setIsEditingDeliveryName(true)}
                    >
                      <span className={styles.deliveryName}>{deliveryName}</span>
                      <SequencePencilIcon className={styles.deliveryNameIcon} />
                    </button>
                  )}
                </span>
              ) : (
                <span className={styles.deliveryName}>
                  {campaignData?.deliveryName.replace('(', '\n(')}
                </span>
              )}
              {isRedesignEnabled && medium && (
                <span className={styles.mediumPill}>
                  <MediumPillIcon />
                  {getDripMediumName(medium)}
                </span>
              )}
              <p
                className={styles.status}
                style={{ color: getCampaignStatusColor(customDeliveryStatus) }}
              >
                <span className={styles.dot}>
                  <Dot fill={getCampaignStatusColor(customDeliveryStatus)} />
                </span>
                {customDeliveryStatus}
              </p>
              <Link className={styles.closeButton} to="/custom-campaigns">
                <CloseIcon />
              </Link>
            </div>
            <div className={styles.controls}>
              <div className={isRedesignEnabled ? styles.wizardSteps : styles.steps}>
                {wizardSteps.map((step, index) => {
                  if (!isRedesignEnabled) {
                    return (
                      <button
                        key={step}
                        onClick={() => setActiveStep(step)}
                        className={step === activeStep ? styles.activeStep : styles.step}
                      >
                        <span className={styles.paginatorNo}>{stepNames[step]}</span>
                      </button>
                    )
                  }

                  const isCompleted = index < activeStepIndex

                  return (
                    <button
                      key={step}
                      onClick={() => setActiveStep(step)}
                      className={
                        step === activeStep
                          ? styles.wizardStepActive
                          : isCompleted
                          ? styles.wizardStepCompleted
                          : styles.wizardStep
                      }
                    >
                      <span className={styles.wizardStepBadge}>
                        {isCompleted ? <SequenceReadyIcon /> : index + 1}
                      </span>
                      <span className={styles.wizardStepLabel}>{stepNames[step]}</span>
                    </button>
                  )
                })}
              </div>
            </div>
          </div>
          <div
            className={[
              styles.dialogBody,
              isRedesignEnabled ? styles.dialogBodyWizard : '',
              !isRedesignEnabled && isSequenceStepActive ? styles.dialogBodySequence : '',
              activeStep === steps.email ? styles.dialogBodyEmail : ''
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {activeStep === steps.channelStrategy && (
              <ChannelStrategyStep
                medium={medium}
                setMedium={setMedium}
                isEditable={canModifySetup}
              />
            )}
            {activeStep === steps.campaignSetup && (
              <CampaignSetup
                audienceId={audienceId}
                audiences={audiences}
                isEdibable={canModifySetup}
                deliveryStartDate={deliveryStartDate}
                setDeliveryStartDate={setDeliveryStartDate}
                setDeliveryFinishDate={setDeliveryFinishDate}
                deliveryFinishDate={deliveryFinishDate}
                setAudienceId={setAudienceId}
                setMedium={setMedium}
                medium={medium}
                isAudienceCalculating={isAudienceCalculating}
                isDripSequenceFeatureEnabled={isDripSequenceFeatureEnabled}
                isSequenceEnabled={isSequenceEnabled}
                setIsSequenceEnabled={setIsSequenceEnabled}
              />
            )}
            {activeStep === steps.dripSequence && (
              <DripSequenceFlow
                steps={sequenceSteps}
                onStepsChange={setSequenceSteps}
                campaignId={Number(id)}
                campaignName={deliveryName}
                medium={medium}
                stepErrors={sequenceValidation.errors}
                onPersistDraft={persistDraftBeforeTestSms}
              />
            )}
            {activeStep === steps.mainTemplate && (
              <MainTemplate
                messageTemplate={messageTemplate}
                onTextChange={handleTextChange}
                usedAllTags={usedAllTags}
                setUsedAllTags={setUsedAllTags}
                setMessageMedia={setMessageMedia}
                messageMedia={messageMedia}
                isAllTagsRequired={true}
                mediaTag={mediaTag}
                requiredTagIds={requiredTagIds}
                isMediaRequired={true}
                customUrl={customUrl}
                setCustomUrl={setCustomUrl}
                isCustomUrlAllowed={true}
                contentId={Number(id)}
                onBeforeSendTestSms={persistDraftBeforeTestSms}
                requireEngagePlusForImage={false}
              />
            )}
            {activeStep === steps.mainPromo && (
              <MainPromo
                promotionCode={promotionCode}
                setPromotionCode={setPromotionCode}
                minNightsToStay={minNightsToStay}
                setMinNightsToStay={setMinNightsToStay}
                maxDiscountPercentage={maxDiscountPercentage}
                setMaxDiscountPercentage={setMaxDiscountPercentage}
                setPromoTagline={setPromoTagline}
                promoTagline={promoTagline}
                hasPromoValidityDate={hasPromoValidityDate}
                setHasPromoValidityDate={setHasPromoValidityDate}
                promoValidityStartDate={promoValidityStartDate}
                setPromoValidityStartDate={setPromoValidityStartDate}
                promoValidityEndDate={promoValidityEndDate}
                setPromoValidityEndDate={setPromoValidityEndDate}
              />
            )}
            {activeStep === steps.promoteLaunch && (
              <PromoteLaunchStep
                preview={<PromoPreview promoTagline={promoTagline} />}
                audiences={audiences}
                audienceId={audienceId}
                setAudienceId={setAudienceId}
                isAudienceCalculating={isAudienceCalculating}
                isEditable={canModifySetup}
                medium={medium}
                deliveryStartDate={deliveryStartDate}
                setDeliveryStartDate={setDeliveryStartDate}
                deliveryFinishDate={deliveryFinishDate}
                setDeliveryFinishDate={setDeliveryFinishDate}
                promotionCode={promotionCode}
                setPromotionCode={setPromotionCode}
                minNightsToStay={minNightsToStay}
                setMinNightsToStay={setMinNightsToStay}
                maxDiscountPercentage={maxDiscountPercentage}
                setMaxDiscountPercentage={setMaxDiscountPercentage}
                promoTagline={promoTagline}
                setPromoTagline={setPromoTagline}
                hasPromoValidityDate={hasPromoValidityDate}
                setHasPromoValidityDate={setHasPromoValidityDate}
                promoValidityStartDate={promoValidityStartDate}
                setPromoValidityStartDate={setPromoValidityStartDate}
                promoValidityEndDate={promoValidityEndDate}
                setPromoValidityEndDate={setPromoValidityEndDate}
              />
            )}
            {activeStep === steps.email && (
              <div className={styles.emailStepContent}>
                <CampaignEmailDesignTemplatePicker
                  contentId={Number(id)}
                  campaignDescription={campaignData?.deliveryName || 'This campaign'}
                  onTemplateApplied={({ templateName }) => {
                    refreshEmailData()
                    addToast(`Applied "${templateName}" to this campaign.`, {
                      appearance: 'success'
                    })
                  }}
                />
                <CampaignEmailCustomizationSection
                  includeInboxFields
                  className={styles.emailCustomizationPanel}
                />
              </div>
            )}
          </div>
          <div className={styles.dialogFooter}>
            {isRedesignEnabled && activeStepIndex > 0 && (
              <SecondaryRegularButton
                className={styles.backButton}
                onClick={() => setActiveStep(wizardSteps[activeStepIndex - 1])}
                disabled={isSaving}
              >
                Back
              </SecondaryRegularButton>
            )}
            {customDeliveryStatus === statusTypes.Draft && (
              <>
                <SecondaryRegularButton
                  onClick={() =>
                    updateCampaign(statusTypes.Draft, { reloadCampaignsList: true })
                  }
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'Save changes'}
                </SecondaryRegularButton>
                {nextStep ? (
                  <PrimaryRegularButton
                    className={styles.nextButton}
                    onClick={handleNext}
                    disabled={isNextDisabled || isSaving}
                  >
                    Next
                  </PrimaryRegularButton>
                ) : (
                  <PrimaryRegularButton
                    disabled={!isValidated || isSaving}
                    onClick={checkMediaAndStartCampaign}
                  >
                    Start campaign
                  </PrimaryRegularButton>
                )}
              </>
            )}
            {customDeliveryStatus === statusTypes.Approved && (
              <>
                <SecondaryRegularButton
                  onClick={() =>
                    updateCampaign(statusTypes.Draft, { reloadCampaignsList: true })
                  }
                  disabled={isSaving}
                >
                  Convert to draft
                </SecondaryRegularButton>
                <PrimaryRegularButton
                  disabled={!isValidated || isSaving}
                  onClick={() => updateCampaign(null, { reloadCampaignsList: true })}
                >
                  {isSaving ? 'Saving...' : 'Save campaign'}
                </PrimaryRegularButton>
              </>
            )}
            {customDeliveryStatus === statusTypes.Started && (
              <>
                <SecondaryRegularButton
                  onClick={() =>
                    updateCampaign(statusTypes.Interrupted, {
                      reloadCampaignsList: true
                    })
                  }
                  disabled={isSaving}
                >
                  Abort campaign
                </SecondaryRegularButton>
                <PrimaryRegularButton
                  disabled={!isValidated || isSaving}
                  onClick={() => updateCampaign(null, { reloadCampaignsList: true })}
                >
                  {isSaving ? 'Saving...' : 'Save campaign'}
                </PrimaryRegularButton>
              </>
            )}
          </div>
        </div>
        {isSidePreviewVisible && (
          <div
            className={
              activeStep === steps.email
                ? `${styles.previewHolder} ${styles.previewHolderEmail}`
                : styles.previewHolder
            }
          >
            {activeStep !== steps.email && dripMediumOptionsWithText.includes(medium) && (
              <TextPreview>
                <>
                  <TextPreviewBubble
                    defaultData={{ userName, hotelName }}
                    hasLink
                    customLink={customUrl}
                    text={`${messageTemplate}`}
                  />
                  {messageMedia && isCampaignImageVisible(messageMedia) && (
                    <img
                      src={getImgUrlFromCDN(messageMedia, mediaTag, hotelId)}
                      alt="campaign media"
                      className={styles.campaignMedia}
                    />
                  )}
                  {messageMedia && getExtensionFromUrl(messageMedia) === 'pdf' && (
                    <div className={styles.campaignFile}>
                      <FileIcon extension="pdf" />
                      <span />
                    </div>
                  )}
                  <TextPreviewBubble defaultData={{ userName, hotelName }} />
                </>
              </TextPreview>
            )}
            {activeStep === steps.email && (
              <EmailPreview
                isEditable={false}
                holderClassName={styles.dripEmailPreviewHolder}
                testHolderClassName={styles.dripEmailTestHolder}
                skipInitialSectionReset
                hideEditContentButton
              >
                <TestEmail />
              </EmailPreview>
            )}
            {medium === dripMediumOptions.Email && activeStep === steps.mainPromo && (
              <EmailPreview isEditable={false}>
                <TestEmail />
              </EmailPreview>
            )}
          </div>
        )}
      </div>
    </Dialog>
  )
}

const EditCampaignWithProvider = () => {
  const { id } = useParams()

  return (
    <EmailEditorProvider contentId={id} contentCategory="Custom" mode="campaignBody">
      <EditCampaign />
    </EmailEditorProvider>
  )
}

export default EditCampaignWithProvider
